import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../store/authStore';
import { loginSchema, type LoginFormData } from '../../lib/validationSchemas';
import { generateFingerprint } from '../../lib/fingerprintUtils';
import SEO from '../../components/seo/SEO';

export default function LoginPage() {
  const { t, i18n } = useTranslation('auth');
  const navigate = useNavigate();
  const { login, error, clearError, isLoading } = useAuthStore();
  const [deviceFingerprint, setDeviceFingerprint] = useState<string | null>(null);

  useEffect(() => {
    generateFingerprint().then(setDeviceFingerprint);
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = async (data: LoginFormData) => {
    clearError();

    try {
      await login({ ...data, device_fingerprint: deviceFingerprint });
      navigate('/dashboard');
    } catch {
      // Error is handled by the store
    }
  };

  return (
    <>
      <SEO
        title="Login | Axetra"
        description="Sign in to your Axetra account."
        noindex={true}
      />
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gradient mb-2">
          {t('login.title')}
        </h2>
        <p className="text-gray-600">{t('login.welcome_message')}</p>
      </div>

      {error && (
        <div className="alert alert-error mb-6">
          <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          <span>{error.toLowerCase().includes('credentials') ? t('errors.invalid_credentials') : error}</span>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        <div>
          <label htmlFor="email" className="label">
            {t('login.email_label')}
          </label>
          <div className="mt-1">
            <input
              id="email"
              type="email"
              autoComplete="email"
              {...register('email')}
              className={`input ${errors.email ? 'input-error' : ''}`}
            />
            {errors.email && (
              <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                {errors.email.message}
              </p>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="password" className="label">
            {t('login.password_label')}
          </label>
          <div className="mt-1">
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              {...register('password')}
              className={`input ${errors.password ? 'input-error' : ''}`}
            />
            {errors.password && (
              <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                {errors.password.message}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end">
          <Link
            to={`/${i18n.language}/forgot-password`}
            className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
          >
            {t('login.forgot_password')}
          </Link>
        </div>

        <div className="pt-2">
          <button
            type="submit"
            disabled={isLoading}
            className="btn btn-primary w-full btn-lg"
          >
            {isLoading && (
              <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            )}
            {isLoading ? t('login.submitting') : t('login.submit_button')}
          </button>
        </div>
      </form>

      <div className="mt-8 text-center">
        <p className="text-sm text-gray-600">
          {t('login.no_account')}{' '}
          <Link
            to={`/${i18n.language}/register`}
            className="font-semibold text-primary-600 hover:text-primary-700 transition-colors"
          >
            {t('login.signup_link')}
          </Link>
        </p>
      </div>
    </>
  );
}
